import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  TextField, 
  Button, 
  Alert, 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  CircularProgress,
  FormHelperText,
  AlertTitle,
  Divider,
  Card,
  CardContent,
  CardActions,
  Link as MuiLink,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import StarIcon from '@mui/icons-material/Star';
import GroupIcon from '@mui/icons-material/Group';
import SchoolIcon from '@mui/icons-material/School';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EmailIcon from '@mui/icons-material/Email';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, startOfDay, addDays, isValid } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, Timestamp, query, where, getDocs, orderBy } from 'firebase/firestore';
import { Stack } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { translations } from '../translations';
import { PersonOutline, CalendarToday, LibraryBooks, MenuBook, LockOpen, CheckCircleOutline, AccessTime, Settings, Person, Edit, Lock as LockIcon } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import emailjs from '@emailjs/browser';
import { useLanguage } from '../contexts/LanguageContext';
import PageHeader from '../components/PageHeader';
import { getAuth, updateProfile, updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';

const availableTimeSlots = [
  '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00'
];

const premiumPackage = {
  name: 'Persoonlijk Ontwikkelingsplan',
  price: 750,
  duration: '1 jaar',
  sessions: 10,
  description: 'Complete begeleiding bij het maken en implementeren van je persoonlijke ontwikkelingsplan',
  features: [
    'Onbeperkte toegang tot alle premium materialen gedurende 1 jaar',
    '10 individuele coaching sessies',
    'Persoonlijk ontwikkelplan op maat',
    'Voortgangsevaluaties',
    'E-mail ondersteuning tijdens het hele traject'
  ]
};

const trainingen = [
  { 
    name: 'Groepstrainingen', 
    path: '/diensten/groepstrainingen', 
    description: 'Neem deel aan interactieve groepssessies voor persoonlijke en professionele groei.', 
    icon: GroupIcon 
  },
  { 
    name: 'Workshops', 
    path: '/diensten/workshops', 
    description: 'Praktijkgerichte workshops over specifieke thema\'s zoals timemanagement of communicatie.', 
    icon: SchoolIcon 
  },
];

interface UserAppointment {
  id: string;
  date: string;
  time: string;
  type: string;
}

const AfspraakComponent = () => {
  const { currentUser, isPremiumUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [userAppointments, setUserAppointments] = useState<UserAppointment[]>([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(true);
  const [appointmentsError, setAppointmentsError] = useState<string>('');
  const [appointmentType, setAppointmentType] = useState<string>('Losse Coachingsessie');
  const [notes, setNotes] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const { language } = useLanguage();
  const t = translations[language];
  
  // New state variables for settings
  const [activeTab, setActiveTab] = useState(0);
  const [displayName, setDisplayName] = useState(currentUser?.displayName || '');
  const [email, setEmail] = useState(currentUser?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [settingsSuccess, setSettingsSuccess] = useState('');
  const [settingsError, setSettingsError] = useState('');
  const [settingsLoading, setSettingsLoading] = useState(false);

  const hasPremium = isPremiumUser();

  const diensten = [
    {
      name: t.services.individueleCoaching,
      icon: <PersonOutline sx={{ fontSize: 40, color: 'inherit' }} />,
      description: 'Persoonlijke 1-op-1 begeleiding.',
      path: '/diensten/individuele-coaching',
      color: 'primary.main'
    },
    {
      name: t.services.workshops,
      icon: <CalendarToday sx={{ fontSize: 40, color: 'inherit' }} />,
      description: 'Inspirerende bijeenkomsten en interactieve sessies.',
      path: '/diensten/workshops',
      color: 'secondary.main'
    },
    {
      name: t.services.lesgeven,
      icon: <SchoolIcon sx={{ fontSize: 40, color: 'inherit' }} />,
      description: 'Lezingen, colleges en presentaties.',
      path: '/diensten/lesgeven',
      color: 'warning.main'
    }
  ];

  useEffect(() => {
    const fetchUserAppointments = async () => {
      if (!currentUser) return;
      setAppointmentsLoading(true);
      setAppointmentsError('');
      try {
        const todayTimestamp = Timestamp.fromDate(startOfDay(new Date()));
        const appointmentsRef = collection(db, "appointments");
        const q = query(
          appointmentsRef,
          where("userId", "==", currentUser.uid),
          where("status", "==", "confirmed"),
          where("appointmentDate", ">=", todayTimestamp),
          orderBy("appointmentDate", "asc"),
          orderBy("appointmentTime", "asc")
        );
        
        const querySnapshot = await getDocs(q);
        const fetchedAppointments = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            date: data.appointmentDate ? format(data.appointmentDate.toDate(), 'EEEE d MMMM yyyy', { locale: nl }) : 'Ongeldige datum',
            time: data.appointmentTime || '-',
            type: data.appointmentType || 'Afspraak'
          } as UserAppointment;
        });
        console.log("Fetched user appointments:", fetchedAppointments);
        setUserAppointments(fetchedAppointments);
      } catch (err) {
        console.error("Error fetching user appointments: ", err);
        setAppointmentsError("Kon uw aankomende afspraken niet laden. Probeer het later opnieuw.");
      } finally {
        setAppointmentsLoading(false);
      }
    };

    if (currentUser) {
      fetchUserAppointments();
    } else {
      setAppointmentsLoading(false);
    }
  }, [currentUser]);

  // --- Event Handlers ---

  // Restore form submit handler (modified)
  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!currentUser) {
      setError("U moet ingelogd zijn om een afspraak aan te vragen.");
      return;
    }

    if (!appointmentType) {
      setError("Selecteer alstublieft het type gesprek.");
      return;
    }

    setLoading(true);

    try {
      // Construct data for a REQUEST (not a confirmed booking)
      // No date/time selected by user
      const appointmentRequestData = {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: currentUser.fullName || 'Niet opgegeven', 
        // appointmentDate: null, // Do not include date/time yet
        // appointmentTime: null,
        appointmentType: appointmentType,
        notes: notes, // Include user preferences/notes
        status: 'pending_user_request', // Use a distinct status for user requests vs coach-created
        requestedAt: serverTimestamp()
      };

      // Store in 'appointments' collection (or a dedicated 'appointmentRequests' collection?)
      // For now, using 'appointments' with a different status.
      const docRef = await addDoc(collection(db, "appointments"), appointmentRequestData);
      console.log("Appointment request (from user) submitted with ID: ", docRef.id);
      setSuccess(`Uw verzoek voor '${appointmentType}' is succesvol ingediend. Ik neem contact met u op om een specifieke datum en tijd af te stemmen.`);
      
      // Reset form fields
      setAppointmentType('Losse Coachingsessie');
      setNotes('');

    } catch (err) {
      console.error("Error submitting appointment request: ", err);
      setError("Kon het afspraakverzoek niet indienen. Probeer het later opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  // New handler for tab changes
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  
  // New handler for updating profile
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    
    setSettingsLoading(true);
    setSettingsError('');
    setSettingsSuccess('');
    
    try {
      const auth = getAuth();
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: displayName
        });
        setSettingsSuccess('Profiel bijgewerkt!');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setSettingsError('Er is een fout opgetreden bij het bijwerken van uw profiel.');
    } finally {
      setSettingsLoading(false);
    }
  };
  
  // New handler for updating password
  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !currentUser.email) return;
    
    if (newPassword !== confirmPassword) {
      setSettingsError('Nieuwe wachtwoorden komen niet overeen.');
      return;
    }
    
    if (newPassword.length < 6) {
      setSettingsError('Nieuw wachtwoord moet minimaal 6 tekens bevatten.');
      return;
    }
    
    setSettingsLoading(true);
    setSettingsError('');
    setSettingsSuccess('');
    
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (user && user.email) {
        // Re-authenticate user before changing password
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        await reauthenticateWithCredential(user, credential);
        
        // Update password
        await updatePassword(user, newPassword);
        
        setSettingsSuccess('Wachtwoord bijgewerkt!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (error: any) {
      console.error('Error updating password:', error);
      if (error.code === 'auth/wrong-password') {
        setSettingsError('Huidig wachtwoord is onjuist.');
      } else {
        setSettingsError('Er is een fout opgetreden bij het bijwerken van uw wachtwoord.');
      }
    } finally {
      setSettingsLoading(false);
    }
  };

  // --- Render Logic ---

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!authLoading && !currentUser) {
    return <Alert severity="error">Kon gebruikersinformatie niet laden.</Alert>;
  }

  return (
    <>
      <PageHeader
        title={`Welkom ${currentUser?.displayName || currentUser?.email || 'terug'}`}
        subtitle="in je Portaal"
      />

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* === Non-Premium CTA Section === */} 
        {!hasPremium && (
          <Paper elevation={2} sx={{ 
             p: {xs: 2, md: 4}, 
             mb: 4, 
             bgcolor: 'primary.lighter', // Use a subtle background
             border: `1px solid ${theme.palette.primary.light}`,
             borderRadius: 2 
          }}>
             <Typography variant="h5" component="h2" color="primary.dark" gutterBottom sx={{ fontWeight: 600 }}>
               Ontgrendel Meer Mogelijkheden
             </Typography>
             <Typography variant="body1" paragraph sx={{ color: 'text.primary' }}>
               Krijg toegang tot de fundamentele zelfcoachings-opdrachten om een start te maken met jouw ontwikkeling.
               Maak je eerste afspraak of ontgrendel direct het Essential pakket.
             </Typography>
             
             <Divider sx={{ my: 2 }} />
             
             <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Essential Pakket (€29 incl. BTW):
             </Typography>
             <List dense disablePadding sx={{ mb: 2 }}>
               <ListItem sx={{ py: 0.5 }}>
                 <ListItemIcon sx={{ minWidth: 32 }}>
                    <MenuBook color="primary" fontSize="small" />
                 </ListItemIcon>
                 <ListItemText primary="Fundamentele zelfcoachings-opdrachten" primaryTypographyProps={{ fontSize: '0.95rem' }} />
               </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                 <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleOutline color="primary" fontSize="small" />
                 </ListItemIcon>
                 <ListItemText primary="Ideaal voor een laagdrempelige start" primaryTypographyProps={{ fontSize: '0.95rem' }} />
               </ListItem>
               <ListItem sx={{ py: 0.5 }}>
                 <ListItemIcon sx={{ minWidth: 32 }}>
                    <AccessTime color="primary" fontSize="small" />
                 </ListItemIcon>
                 <ListItemText primary="3 maanden toegang" primaryTypographyProps={{ fontSize: '0.95rem' }} />
               </ListItem>
             </List>
             
             <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
               <Button 
                 variant="contained" 
                 color="primary" 
                 startIcon={<CalendarToday />}
                 onClick={() => navigate('/contact', { 
                   state: { 
                     prefill: {
                       subject: 'Eerste Afspraak Maken ( vanuit Portaal )'
                     }
                   }
                 })}
                 sx={{ flexGrow: 1 }}
               >
                 Maak een Afspraak
               </Button>
               <Button 
                 variant="outlined" 
                 color="primary"
                 startIcon={<LockOpenIcon />}
                 onClick={() => navigate('/contact', { 
                   state: { 
                     prefill: {
                       subject: 'Essential Premium Toegang Aanvraag ( vanuit Portaal ) ',
                       message: 'Ik zou graag toegang willen tot Essential Premium content (€29). Kunt u mij deze toegang geven?'
                     }
                   }
                 })}
                 sx={{ flexGrow: 1 }}
               >
                 Ontgrendel Essential (€29)
               </Button>
             </Box>
           </Paper>
         )}
         {/* === End Non-Premium CTA Section === */} 

        {/* Premium Status Header - Styled as button on right */}
        {hasPremium && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<StarIcon />}
              sx={{
                borderRadius: '20px',
                px: 3
              }}
              component={RouterLink}
              to="/mijn-content"
            >
              Premium Lid
            </Button>
          </Box>
        )}

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            Mijn Aankomende Afspraken
          </Typography>
          {appointmentsLoading ? (
            <CircularProgress size={24} />
          ) : appointmentsError ? (
            <Alert severity="error">{appointmentsError}</Alert>
          ) : userAppointments.length === 0 ? (
            <Alert severity="info">Je hebt geen aankomende afspraken.</Alert>
          ) : (
            <Stack spacing={2}>
              {userAppointments.map((appt) => (
                <Card key={appt.id} variant="outlined">
                  <CardContent>
                    <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                      <EventAvailableIcon sx={{ mr: 1, color: 'primary.main' }} /> {appt.date}
                    </Typography>
                    <Typography sx={{ mb: 1.5 }} color="text.secondary">
                      Om {appt.time} - {appt.type}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Box>

        <Grid container spacing={4}> 
          
          <Grid item xs={12} md={4} order={{ xs: 2, md: 1 }}> 
             <Paper sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
               <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Premium Voordelen
               </Typography>
               <List dense sx={{ mb: 2, pl: 1 }}>
                  <ListItem sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}><StarIcon fontSize="small" color="secondary" /></ListItemIcon>
                    <ListItemText primary="Exclusieve artikelen & tips" />
                  </ListItem>
                  <ListItem sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}><SchoolIcon fontSize="small" color="secondary" /></ListItemIcon>
                    <ListItemText primary="Praktische oefeningen & templates" />
                  </ListItem>
                  <ListItem sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}><DownloadIcon fontSize="small" color="secondary" /></ListItemIcon>
                    <ListItemText primary="Downloadbare werkboeken" />
                  </ListItem>
               </List>
               {!hasPremium && (
                  <Button 
                    component={RouterLink} 
                    to="/premium" 
                    variant="contained" 
                    color="secondary" 
                    size="medium" 
                    fullWidth
                    startIcon={<LockOpenIcon />}
                    sx={{ mb: 3, fontWeight: 600 }}
                  >
                    Upgrade naar Premium
                  </Button>
               )}
               {hasPremium && (
                  <Button 
                    component={RouterLink} 
                    to="/mijn-content" 
                    variant="outlined" 
                    color="primary" 
                    size="medium" 
                    fullWidth
                    startIcon={<LibraryBooks />}
                    sx={{ mb: 3, fontWeight: 600 }}
                  >
                    Naar Mijn Content
                  </Button>
               )}
               
               <Divider sx={{ my: 2 }} />

               <Box sx={{ mb: 3 }}>
                   <Typography variant="subtitle1" sx={{ mb: 1 }}>Downloads</Typography>
                   <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 1, 
                      bgcolor: !hasPremium ? 'grey.100' : 'transparent',
                      position: 'relative',
                      overflow: 'hidden',
                      filter: !hasPremium ? 'blur(3px)' : 'none',
                      userSelect: !hasPremium ? 'none' : 'auto',
                      transition: 'filter 0.3s ease',
                   }}>
                     <Typography variant="body2" color="text.secondary">
                         Hier vind je binnenkort je persoonlijke documenten en huiswerkopdrachten.
                         {!hasPremium && (
                           <>
                              <br/>- Werkboek 'Kernwaarden Ontdekken'
                              <br/>- Template 'Persoonlijk Ontwikkelplan'
                           </>
                         )}
                     </Typography>
                      {!hasPremium && (
                       <Box sx={{ 
                           position: 'absolute', 
                           top: 0, left: 0, right: 0, bottom: 0, 
                           display: 'flex', 
                           alignItems: 'center', 
                           justifyContent: 'center',
                           zIndex: 1, 
                           backdropFilter: 'blur(0px)'
                          }}>
                          <Button 
                              component={RouterLink} 
                              to="/diensten/zelfcoaching?scrollTo=pakketten"
                              variant="contained" 
                              size="small" 
                              startIcon={<LockOpenIcon />}
                              sx={{ fontWeight: 600 }}
                              >
                              Ontgrendel met Premium
                          </Button>
                       </Box>
                     )}
                   </Box>
               </Box>
               
               <Box>
                   <Typography variant="subtitle1" sx={{ mb: 1 }}>Snelle Links</Typography>
                   <Stack spacing={1}>
                      <Button component={RouterLink} to="/diensten/individuele-coaching" size="small" variant="text" sx={{ justifyContent: 'flex-start' }}>
                          Individuele Coaching
                      </Button>
                      <Button component={RouterLink} to="/diensten/zelfcoaching" size="small" variant="text" sx={{ justifyContent: 'flex-start' }}>
                          Zelfcoaching
                      </Button>
                      <Button component={RouterLink} to="/tips" size="small" variant="text" sx={{ justifyContent: 'flex-start' }}>
                          Tips & Advies
                      </Button>
                      <Button component={RouterLink} to="/events" size="small" variant="text" sx={{ justifyContent: 'flex-start' }}>
                          Komende Events
                      </Button>
                   </Stack>
               </Box>
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                   <Typography variant="caption" color="text.secondary">
                       Hulp nodig? <MuiLink component={RouterLink} to="/contact">Neem contact op</MuiLink>.
                   </Typography>
                </Box>
             </Paper>
          </Grid>

          <Grid item xs={12} md={8} order={{ xs: 1, md: 2 }}> 
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange} 
                aria-label="portal tabs"
                sx={{ 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  backgroundColor: 'background.paper',
                }}
              >
                <Tab 
                  label={language === 'nl' ? "Afspraken" : "Appointments"} 
                  icon={<CalendarToday fontSize="small" />} 
                  iconPosition="start"
                  sx={{ textTransform: 'none', py: 2 }}
                />
                <Tab 
                  label={language === 'nl' ? "Instellingen" : "Settings"} 
                  icon={<Settings fontSize="small" />} 
                  iconPosition="start"
                  sx={{ textTransform: 'none', py: 2 }}
                />
              </Tabs>
              
              {/* Appointments Tab Panel */}
              {activeTab === 0 && (
                <Box sx={{ p: 4 }}>
                  <Typography variant="h5" component="h2" gutterBottom>
                    {language === 'nl' ? "Nieuwe Afspraak Aanvragen" : "Request New Appointment"}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    {language === 'nl' 
                      ? "Selecteer het type gesprek of dienst dat je wilt aanvragen. Ik neem contact met je op om een definitieve afspraak in te plannen."
                      : "Select the type of conversation or service you want to request. I will contact you to schedule a definitive appointment."}
                  </Typography>
                  
                  {/* Existing appointment form */}
                  <form onSubmit={handleFormSubmit} noValidate>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <FormControl fullWidth required error={!!error && !appointmentType} disabled={loading} size="small">
                          <InputLabel>{language === 'nl' ? "Type Gesprek/Dienst *" : "Type of Conversation/Service *"}</InputLabel>
                          <Select
                            value={appointmentType}
                            label={language === 'nl' ? "Type Gesprek/Dienst *" : "Type of Conversation/Service *"}
                            onChange={(e) => setAppointmentType(e.target.value)}
                          >
                            <MenuItem value="Losse Coachingsessie">{language === 'nl' ? "Losse Coachingsessie" : "Single Coaching Session"}</MenuItem>
                            <MenuItem value="Platinum Coaching Pakket">{language === 'nl' ? "Start Platinum Coaching Pakket" : "Start Platinum Coaching Package"}</MenuItem>
                          </Select>
                          {!!error && !appointmentType && <FormHelperText sx={{ ml: 2 }}>{language === 'nl' ? "Selecteer een type dienst" : "Select a type of service"}</FormHelperText>}
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          label={language === 'nl' ? "Opmerkingen" : "Comments"}
                          multiline
                          rows={4}
                          fullWidth
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          variant="outlined"
                          disabled={loading}
                          size="small"
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                        {success && 
                          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                            <AlertTitle>{language === 'nl' ? "Verzoek Ingediend" : "Request Submitted"}</AlertTitle>
                            {success}
                          </Alert>
                        }
                        
                        <Button 
                          type="submit" 
                          variant="contained" 
                          color="primary" 
                          size="large" 
                          fullWidth
                          disabled={loading}
                          startIcon={loading ? <CircularProgress size={20} color="inherit"/> : null}
                        >
                          {loading 
                            ? (language === 'nl' ? "Verzoek Indienen..." : "Submitting Request...") 
                            : (language === 'nl' ? "Afspraakverzoek Indienen" : "Submit Appointment Request")}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </Box>
              )}
              
              {/* Settings Tab Panel */}
              {activeTab === 1 && (
                <Box sx={{ p: 4 }}>
                  <Typography variant="h5" component="h2" sx={{ mb: 3 }}>
                    {language === 'nl' ? "Mijn Instellingen" : "My Settings"}
                  </Typography>
                  
                  {settingsError && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSettingsError('')}>
                      {settingsError}
                    </Alert>
                  )}
                  
                  {settingsSuccess && (
                    <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSettingsSuccess('')}>
                      {settingsSuccess}
                    </Alert>
                  )}
                  
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person fontSize="small" /> {language === 'nl' ? "Profielgegevens" : "Profile Information"}
                    </Typography>
                    
                    <form onSubmit={handleUpdateProfile}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <TextField
                            label={language === 'nl' ? "Naam" : "Name"}
                            fullWidth
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            variant="outlined"
                            disabled={settingsLoading}
                            size="small"
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <TextField
                            label="Email"
                            fullWidth
                            value={email}
                            disabled={true}
                            variant="outlined"
                            size="small"
                            helperText={language === 'nl' ? "Email kan niet worden gewijzigd" : "Email cannot be changed"}
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={settingsLoading || displayName === currentUser?.displayName}
                            startIcon={settingsLoading ? <CircularProgress size={20} /> : <Edit />}
                          >
                            {language === 'nl' ? "Profiel Bijwerken" : "Update Profile"}
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  </Box>
                  
                  <Divider sx={{ my: 4 }} />
                  
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LockIcon fontSize="small" /> {language === 'nl' ? "Wachtwoord Wijzigen" : "Change Password"}
                    </Typography>
                    
                    <form onSubmit={handleUpdatePassword}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <TextField
                            label={language === 'nl' ? "Huidig Wachtwoord" : "Current Password"}
                            type="password"
                            fullWidth
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            variant="outlined"
                            required
                            disabled={settingsLoading}
                            size="small"
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={language === 'nl' ? "Nieuw Wachtwoord" : "New Password"}
                            type="password"
                            fullWidth
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            variant="outlined"
                            required
                            disabled={settingsLoading}
                            size="small"
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={language === 'nl' ? "Bevestig Nieuw Wachtwoord" : "Confirm New Password"}
                            type="password"
                            fullWidth
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            variant="outlined"
                            required
                            disabled={settingsLoading}
                            size="small"
                            error={newPassword !== confirmPassword && confirmPassword !== ''}
                            helperText={newPassword !== confirmPassword && confirmPassword !== '' 
                              ? (language === 'nl' ? "Wachtwoorden komen niet overeen" : "Passwords do not match")
                              : ''}
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={settingsLoading || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                            startIcon={settingsLoading ? <CircularProgress size={20} /> : <LockIcon />}
                          >
                            {language === 'nl' ? "Wachtwoord Wijzigen" : "Change Password"}
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default AfspraakComponent;